import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";

const REASONS = [
  { id: "fraud", label: "Fraudulent or scam", fr: "Frauduleux ou arnaque" },
  { id: "offensive", label: "Offensive or inappropriate", fr: "Offensant ou inapproprié" },
  { id: "prohibited", label: "Prohibited / illegal animal", fr: "Animal interdit / illégal" },
  { id: "spam", label: "Spam or duplicate", fr: "Pourriel ou doublon" },
  { id: "other", label: "Something else", fr: "Autre chose" },
];

const TXT = {
  en: { trigger: "Report this ad", title: "Report this ad", desc: "Reports are private and reviewed by CritterPosts administrators.", placeholder: "Add any details that will help us review (optional)", submit: "Submit report", sent: "Report submitted. Our team will review it. Thank you." },
  fr: { trigger: "Signaler cette annonce", title: "Signaler cette annonce", desc: "Les signalements sont privés et examinés par les administrateurs de CritterPosts.", placeholder: "Ajoutez des détails qui nous aideront (facultatif)", submit: "Envoyer le signalement", sent: "Signalement envoyé. Notre équipe l'examinera. Merci." },
};

export const ReportDialog = ({ listingId }) => {
  const { user, setAuthOpen } = useAuth();
  const { lang } = useLanguage();
  const t = TXT[lang] || TXT.en;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("fraud");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);

  const trigger = () => {
    if (!user) return setAuthOpen(true);
    setOpen(true);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const label = REASONS.find((r) => r.id === reason)?.label || reason;
      await api.post(`/listings/${listingId}/report`, { reason: label, details });
      toast.success(t.sent);
      setOpen(false);
      setDetails("");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={trigger} data-testid="report-ad-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-600 transition-colors">
        <Flag size={14} /> {t.trigger}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#12234A] flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-red-50 flex items-center justify-center"><Flag size={16} className="text-red-600" /></span>
              {t.title}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {t.desc}
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={reason} onValueChange={setReason} className="space-y-1">
            {REASONS.map((r) => (
              <label key={r.id} htmlFor={`reason-${r.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 cursor-pointer hover:border-[#F5551E] has-[:checked]:border-[#F5551E] has-[:checked]:bg-[#FFF4F0]">
                <RadioGroupItem value={r.id} id={`reason-${r.id}`} data-testid={`report-reason-${r.id}`} />
                <span className="text-sm font-medium text-[#12234A]">{lang === "fr" ? r.fr : r.label}</span>
              </label>
            ))}
          </RadioGroup>
          <Textarea value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details" rows={3} className="rounded-xl" placeholder={t.placeholder} />
          <Button onClick={submit} disabled={loading} data-testid="submit-report-btn" className="w-full h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold gap-2">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Flag size={16} />} {t.submit}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
